<script setup lang="ts">
import { computed, ref, watch, inject, Ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { DateTime } from 'luxon';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useCityStore } from '@/store/CityStore';
import { helpers, required } from '@vuelidate/validators';
import { STATUS_OPEN } from '@/constants';
import { resetTimer } from '@/utils/dateUtils';
import { useVuelidate } from '@vuelidate/core';
import { useToast } from 'primevue/usetoast';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import HotelDateTypes from '@/components/modal/hotels/HotelDateTypes.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import TourTarget from '@/components/modal/tour/TourTarget.vue';

const dialogRef = inject('dialogRef') as Ref;
const onApply = dialogRef?.value?.options?.emits?.onApply;
const isEdit = dialogRef?.value?.options?.data?.isEdit;
const tourGatewayDestData = dialogRef?.value?.options?.data?.tourGatewayDestData;

const currentLanguage = getCurrentLocale();
const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const cityStore = useCityStore();

const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const getCityGateways = ref([]);
const filterCityGateways = ref([]);
const getCityDestinations = ref([]);
const filterCityDestinations = ref([]);
const isLoading = ref(false);

const handleCallSubmit = async () => {
  const clonePayload = cloneDeep(tourForm.value);
  const payload: any = {
    ...clonePayload,
    quoteDate: formatDateZeroHour(clonePayload.quoteDate),
    travelDate: formatDateZeroHour(clonePayload.travelDate),
    blackoutDate: formatDateZeroHour(clonePayload.blackoutDate)
  };
  const submitPayload = isEdit ? formatPayloadEdit(payload) : payload;
  v$.value.$validate();
  if (!v$.value.$error) {
    onApply(submitPayload);
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.fill_required') });
  }
};

const getSkString = (obj: any) => {
  const fields = [obj.destCode ?? 'null', obj.destId ?? 'null', obj.gateId ?? 'null', obj.parentDestId ?? 'null', obj.parentTourId ?? 'null', obj.gateReferenceType ?? 'null', obj.destReferenceType ?? 'null'];
  return fields.join('#');
};

const formatPayloadEdit = (payload: any) => {
  return {
    data: {
      ...payload,
      sk: getSkString(payload)
    },
    oldCompositeKey: {
      pk: payload.pk,
      sk: payload.sk
    }
  };
};

const formatDateZeroHour = (listDate: any) => {
  if (listDate?.length) {
    return listDate?.map((date: any) => {
      return {
        start: resetTimer(date.start),
        end: resetTimer(date.end)
      };
    });
  } else {
    return [];
  }
};

const formatEditData = (data: any) => {
  return {
    ...data,
    gateway: [],
    destination: [],
    quoteDate: formatListDate(data.quoteDate),
    travelDate: formatListDate(data.travelDate),
    blackoutDate: formatListDate(data.blackoutDate)
  };
};

const formatListDate = (listDate: Array<any>) => {
  if (listDate?.length) {
    return listDate?.map((date: Date) => {
      return {
        start: new Date(DateTime.fromISO(date.start)),
        end: new Date(DateTime.fromISO(date.end))
      };
    });
  } else {
    return [];
  }
};

const tourForm = ref<any>(
  tourGatewayDestData
    ? cloneDeep(formatEditData(tourGatewayDestData))
    : {
        gateway: [],
        gateCode: '',
        gateId: '',
        gateNameEn: '',
        gateNameFr: '',
        gateReferenceType: '',
        destination: [],
        destCode: '',
        destId: '',
        destNameEn: '',
        destNameFr: '',
        destReferenceType: '',
        parentTourId: '',
        tourNameEn: '',
        tourNameFr: '',
        quoteDate: [],
        travelDate: [],
        blackoutDate: [],
        durations: [],
        parentDestId: '',
        gateCountryEn: '',
        gateCountryFr: '',
        destCountryEn: '',
        destCountryFr: '',
        typeInput: 'MANUAL',
        combinationStatus: STATUS_OPEN,
        deleteStatus: false,
        isTourOnly: true
      }
);
const tourStatusObject = ref({
  status: tourForm.value.combinationStatus
});

const generateDurations = computed(() => {
  const durationArray = [];
  for (let start = 1; start <= 45; start++) {
    durationArray.push({ label: start });
  }
  return durationArray;
});

const rules = ref({
  gateway: { required },
  destination: { required },
  parentTourId: { required },
  tourNameEn: { required },
  tourNameFr: { required },
  quoteDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  },
  travelDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  },
  blackoutDate: {
    $each: helpers.forEach({
      end: { required: helpers.withMessage(t('validate.required'), required) },
      start: { required: helpers.withMessage(t('validate.required'), required) }
    })
  },
  durations: { required }
});

let v$: any = useVuelidate(rules.value, tourForm.value);

const removeDate = (type: string, index: number) => {
  tourForm.value[type] = tourForm.value[type].filter((_item: any, indexDate: number) => indexDate !== index);
};

const addNewDate = (type: string) => {
  tourForm.value[type].push({
    start: 0,
    end: 0
  });
};

const findEditIndexGate = (gateway: Array<any>) => {
  const gateForm = tourForm.value;
  gateway.map((gate: any) => {
    if (gate.gateCode == gateForm.gateCode && gate.gateId == gateForm.gateId && gate.referenceType == gateForm.gateReferenceType) {
      gateForm.gateway = gate;
    }
  });
};

const findEditIndexDestination = (destination: Array<any>) => {
  const destForm = tourForm.value;
  destination.map((dest: any) => {
    if (dest.destCode === destForm.destCode && dest.destId === destForm.destId && dest.referenceType === destForm.destReferenceType) {
      destForm.destination = dest;
    }
  });
};

const searchGateway = (event?: any) => {
  if (!event?.query?.trim().length) {
    filterCityGateways.value = [...getCityGateways.value.slice(0, 100)];
  } else {
    filterCityGateways.value = getCityGateways.value.filter((gateways) => {
      return (
        gateways.gateCode.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateways[`cityName${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateways[`region${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateways[`gateCountry${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateways[`gateName${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateways.referenceType.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const searchDestination = (event?: any) => {
  if (!event?.query?.trim().length) {
    filterCityDestinations.value = [...getCityDestinations.value.slice(0, 100)];
  } else {
    filterCityDestinations.value = getCityDestinations.value.filter((destination) => {
      return (
        destination.destCode.toLowerCase().startsWith(event.query.toLowerCase()) ||
        destination[`cityName${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        destination[`region${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        destination[`destCountry${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        destination[`destName${capitalize(currentLanguage)}`].toLowerCase().startsWith(event.query.toLowerCase()) ||
        destination.referenceType.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const selectGateway = (event: any) => {
  const gateway = event.value;
  if (gateway && Object.keys(gateway).length) {
    for (const gateKey in gateway) {
      if (gateKey !== 'parentTourId' && gateKey !== 'parentDestId') {
        tourForm.value[gateKey] = gateway[gateKey];
      }
      tourForm.value.gateReferenceType = gateway.referenceType;
    }
  } else {
    return clearGateway();
  }
};

const selectDestination = (event: any) => {
  const destination = event.value;
  if (destination && Object.keys(destination).length) {
    for (const destKey in destination) {
      if (destKey !== 'parentTourId') {
        tourForm.value[destKey] = destination[destKey];
      }
      tourForm.value.destReferenceType = destination.referenceType;
    }
  } else {
    return clearDestination();
  }
};

const clearGateway = () => {
  tourForm.value.gateway = [];
  tourForm.value.gateId = '';
  tourForm.value.gateCode = '';
  tourForm.value.gateNameEn = '';
  tourForm.value.gateNameFr = '';
  tourForm.value.gateCountryEn = '';
  tourForm.value.gateCountryFr = '';
  tourForm.value.gateReferenceType = '';
};

const clearDestination = () => {
  tourForm.value.destination = [];
  tourForm.value.destId = '';
  tourForm.value.destCode = '';
  tourForm.value.destNameEn = '';
  tourForm.value.destNameFr = '';
  tourForm.value.parentDestId = '';
  tourForm.value.destCountryEn = '';
  tourForm.value.destCountryFr = '';
  tourForm.value.destReferenceType = '';
};

const getFilterCityDestinations = async () => {
  isLoading.value = true;
  cityStore
    .getCityDestinations()
    .then((res: any) => {
      if (res?.length) {
        getCityDestinations.value = res;
        if (isEdit) {
          findEditIndexDestination(res);
        }
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
      if (isEdit) {
        searchDestination();
      }
    });
};

const getFilterCityGateways = async () => {
  isLoading.value = true;
  cityStore
    .getCityGateways()
    .then((res: any) => {
      if (res?.length) {
        getCityGateways.value = res;
        if (isEdit) {
          findEditIndexGate(res);
        }
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
      if (isEdit) {
        searchGateway();
      }
    });
};

const getDetailAddress = (dataProps: any, type: string) => {
  return dataProps[`cityName${capitalize(currentLanguage)}`] + ', ' + dataProps[`region${capitalize(currentLanguage)}`] + ', ' + dataProps[`${type}Country${capitalize(currentLanguage)}`] + ', ' + dataProps.referenceType;
};

const validateSwitch = (event: any) => {
  if (typeof event == 'object') {
    tourForm.value.combinationStatus = event.value.status;
  }
  return tourForm.value.combinationStatus;
};

const validateCheckbox = (event: any) => {
  tourForm.value.isTourOnly = event;
  return tourForm.value.isTourOnly;
};

watch(tourForm.value, (newValue) => {
  if (newValue) {
    v$ = useVuelidate(rules.value, newValue);
  }
});

onMounted(() => {
  getFilterCityGateways();
  getFilterCityDestinations();
});
</script>

<template>
  <div class="tour-management">
    <div class="gate-dest-add-form">
      <LoadingSpinner v-if="isLoading" />
      <Fluid>
        <div class="field grid">
          <div class="col-6">
            <label for="gateway" class="font-title">{{ $t('common.gateway') }}<span class="text-primary">*</span></label>
            <div class="flex align-items-center">
              <AutoComplete
                v-model="tourForm.gateway"
                :suggestions="filterCityGateways"
                @complete="searchGateway($event)"
                :placeholder="t('common.gateway')"
                :optionLabel="currentLanguage == 'en' ? 'gateNameEn' : 'gateNameFr'"
                completeOnFocus
                @change="selectGateway($event)"
                class="w-10"
                forceSelection
                data-testid="input_gateway"
                :class="v$.gateway.$error ? 'p-invalid' : ''"
              >
                <template #option="slotProps">
                  <div class="flex align-options-center">
                    <div>
                      <div>{{ slotProps.option[`gateName${capitalize(currentLanguage)}`] }} ({{ slotProps.option.gateCode }})</div>
                      <div class="text-xs">{{ getDetailAddress(slotProps.option, 'gate') }}</div>
                    </div>
                  </div>
                </template>
              </AutoComplete>
              <i class="pi pi-times-circle text-primary text-xl ml-1 cursor-pointer ml-3 clear-gateway" @click="clearGateway()"></i>
            </div>
            <p class="text-red-500 pt-1" v-if="v$.gateway.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-2">
            <label for="gateway_airport_code" class="font-title">{{ $t('report.gateway_airport_code') }} </label>
            <InputText id="gateway_airport_code" :placeholder="t('report.gateway_airport_code')" disabled v-model="tourForm.gateCode" class="w-full" />
          </div>
          <div class="col-2">
            <label for="gateway_airport_id" class="font-title">{{ $t('management.tour.gateway_airport_id') }} </label>
            <InputText id="gateway_airport_id" :placeholder="t('management.tour.gateway_airport_id')" disabled v-model="tourForm.gateId" class="w-full" />
          </div>
          <div class="col-2">
            <label for="gateway_country" class="font-title">{{ $t('management.tour.gate_country') }} </label>
            <InputText id="gateway_country" v-tooltip="tourForm[`gateCountry${capitalize(currentLanguage)}`]" :placeholder="t('management.tour.gate_country')" disabled v-model="tourForm[`gateCountry${capitalize(currentLanguage)}`]" class="w-full" />
          </div>
        </div>
        <div class="field grid">
          <div class="col-6">
            <label for="destination" class="font-title">{{ $t('common.destination') }}<span class="text-primary">*</span></label>
            <div class="flex align-items-center">
              <AutoComplete
                v-model="tourForm.destination"
                :suggestions="filterCityDestinations"
                @complete="searchDestination($event)"
                :optionLabel="currentLanguage == 'en' ? 'destNameEn' : 'destNameFr'"
                completeOnFocus
                class="w-10"
                forceSelection
                :placeholder="t('common.destination')"
                @change="selectDestination($event)"
                data-testid="input_destination"
                :class="v$.gateway.$error ? 'p-invalid' : ''"
              >
                <template #option="slotProps">
                  <div>{{ slotProps.option[`destName${capitalize(currentLanguage)}`] }} ({{ slotProps.option.destCode }})</div>
                  <div class="text-xs">{{ getDetailAddress(slotProps.option, 'dest') }}</div>
                </template>
              </AutoComplete>
              <i class="pi pi-times-circle text-primary text-xl ml-1 cursor-pointer ml-3 clear-dest" @click="clearDestination()"></i>
            </div>
            <p class="text-red-500 pt-1" v-if="v$.destination.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-2">
            <label for="destination_airport_code" class="font-title">{{ $t('report.destination_airport_code') }} </label>
            <InputText id="destination_airport_code" :placeholder="t('report.destination_airport_code')" disabled class="w-full" v-model="tourForm.destCode" />
          </div>
          <div class="col-2">
            <label for="destination_airport_id" class="font-title w-full">{{ $t('management.tour.dest_airport_id') }} </label>
            <InputText id="destination_airport_id" :placeholder="t('management.tour.dest_airport_id')" disabled class="w-full" v-model="tourForm.destId" />
          </div>
          <div class="col-2">
            <label for="destination_country" class="font-title w-full"> {{ $t('management.tour.dest_country') }} </label>
            <InputText
              id="destination_country"
              v-tooltip="tourForm[`destCountry${capitalize(currentLanguage)}`]"
              :placeholder="t('management.tour.dest_country')"
              disabled
              class="w-full"
              v-model="tourForm[`destCountry${capitalize(currentLanguage)}`]"
            />
          </div>
        </div>
        <div class="field grid">
          <div class="col-6 flex flex-column">
            <label for="tour_name_en" class="font-title">{{ $t('management.tour.tour_name_en') }}<span class="text-primary">*</span></label>
            <InputText id="tour_name_en" class="w-10" :placeholder="t('management.tour.tour_name_en')" v-model="tourForm.tourNameEn" :class="v$.tourNameEn.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.tourNameEn.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-2">
            <label for="parent_destination_id" class="font-title">{{ $t('management.tour.dest_parent_id') }} </label>
            <InputText id="parent_destination_id" :placeholder="t('management.tour.dest_parent_id')" disabled class="w-full" v-model="tourForm.parentDestId" />
          </div>
          <div class="col-2">
            <label for="parent_tour_id" class="font-title">{{ $t('management.tour.parent_tour_id') }}<span class="text-primary">*</span></label>
            <InputText id="parent_tour_id" class="w-full" :placeholder="t('management.tour.parent_tour_id')" v-model="tourForm.parentTourId" :class="v$.parentTourId.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.parentTourId.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-2">
            <label for="add_duration" class="font-title">{{ $t('common.labels.durations') }}<span class="text-primary">*</span></label>
            <MultiSelect id="add_duration" :placeholder="t('common.labels.durations')" display="chip" :maxSelectedLabels="2" v-model="tourForm.durations" :options="generateDurations" optionLabel="label" optionValue="label" class="w-full">
              <template #option="slotProps">
                <p>
                  {{ slotProps.option.label }}
                </p>
              </template>
            </MultiSelect>
            <p class="text-red-500 pt-1" v-if="v$.durations.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
        <div class="field grid">
          <div class="col-6 flex flex-column">
            <label for="tour_name_fr" class="font-title">{{ $t('management.tour.tour_name_fr') }}<span class="text-primary">*</span></label>
            <InputText id="tour_name_fr" class="w-10" :placeholder="t('management.tour.tour_name_fr')" v-model="tourForm.tourNameFr" :class="v$.tourNameFr.$error ? 'p-invalid' : ''" />
            <p class="text-red-500 pt-1" v-if="v$.tourNameFr.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid border-dot-line">
          <div class="col-4">
            <HotelDateTypes :title="$t('management.tour.quote_date')" :dateRanges="tourForm.quoteDate" @addNewDate="addNewDate('quoteDate')" @removeDate="removeDate('quoteDate', $event)" />
          </div>
          <div class="col-4">
            <HotelDateTypes :title="$t('hotel_contract.travel_date_range')" :dateRanges="tourForm.travelDate" @addNewDate="addNewDate('travelDate')" @removeDate="removeDate('travelDate', $event)" />
          </div>
          <div class="col-4">
            <HotelDateTypes :title="$t('hotel_contract.black_out_date')" :dateRanges="tourForm.blackoutDate" @addNewDate="addNewDate('blackoutDate')" @removeDate="removeDate('blackoutDate', $event)" />
          </div>
        </div>
      </Fluid>
      <Fluid>
        <TourTarget
          :isHideInstaQuote="true"
          :statusPermissions="authorizations.tour_combination_configuration.status_notification"
          :tourOnly="tourForm.isTourOnly"
          :tourStatus="tourStatusObject"
          :isEdit="false"
          :targetLabel="$t('management.tour.create_tour_in')"
          :checkboxLabel="$t('management.tour.tour_only')"
          @validateCheckbox="validateCheckbox($event)"
          @validateSwitch="validateSwitch($event)"
        />
      </Fluid>
      <div class="text-center mt-5">
        <Button :label="$t('payment.labels.submit')" @click="handleCallSubmit()" data-testid="submit-btn"></Button>
      </div>
    </div>
  </div>
</template>
